import { Box, Center, Loader, Text } from "@mantine/core";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { type BackgroundColor, useReaderStore } from "@/store/readerStore";
import type { PdfZoomLevel } from "./PdfReader";
import { type PdfPageOrig, pdfPageReservedHeight } from "./utils/pdfPageHeight";

// Import CSS for text layer and annotation layer
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

// =============================================================================
// Types
// =============================================================================

interface PdfContinuousScrollReaderProps {
  /** Book ID */
  bookId: string;
  /** Total number of pages */
  totalPages: number;
  /** Current page (used for initial scroll position) */
  initialPage?: number;
  /** Zoom level for pages */
  zoomLevel: PdfZoomLevel;
  /** Background color */
  backgroundColor: BackgroundColor;
  /** Gap between pages in pixels */
  pageGap?: number;
  /** Number of pages to preload above/below visible area */
  preloadBuffer?: number;
  /** Search text to highlight */
  searchText?: string;
  /** Callback when the visible page changes (for progress tracking) */
  onPageChange?: (page: number) => void;
  /** Callback when PDF loads */
  onDocumentLoadSuccess?: (pdf: { numPages: number }) => void;
  /** Callback when PDF fails to load */
  onDocumentLoadError?: (error: Error) => void;
  /**
   * Callback ref attached to the scroll container so tap-to-toggle-toolbar
   * navigation (useTouchNav) can listen on the same element that scrolls.
   */
  tapRef?: (el: HTMLDivElement | null) => void;
  /** Panel rendered before the first page (e.g. a "Previous Chapter" panel). */
  leadingSlot?: ReactNode;
  /** Panel rendered after the last page (e.g. a "Next Chapter" panel). */
  trailingSlot?: ReactNode;
  /** Fired on the rising/falling edge of the trailing panel being reached (bottom). */
  onTrailingReachedChange?: (reached: boolean) => void;
  /** Fired on the rising/falling edge of the leading panel being reached (top). */
  onLeadingReachedChange?: (reached: boolean) => void;
}

// =============================================================================
// Constants
// =============================================================================

const BACKGROUND_COLORS: Record<BackgroundColor, string> = {
  black: "#000000",
  gray: "#1a1a1a",
  white: "#ffffff",
};

const DEFAULT_PAGE_GAP = 16;
const SCROLL_DEBOUNCE_MS = 100;
/** Sub-pixel tolerance when deciding the scroll has reached the top/bottom. */
const BOUNDARY_TOLERANCE = 4;

// =============================================================================
// Component
// =============================================================================

/**
 * Continuous scroll PDF reader for vertical reading.
 *
 * Features:
 * - Vertical scrolling with all pages in a single container
 * - Lazy loading: only renders pages that are visible or near-visible
 * - Intersection Observer for efficient visibility tracking
 * - Scroll-based progress tracking
 * - Text selection and search highlighting
 */
export function PdfContinuousScrollReader({
  bookId,
  totalPages,
  initialPage = 1,
  zoomLevel,
  backgroundColor,
  pageGap = DEFAULT_PAGE_GAP,
  preloadBuffer = 0,
  searchText,
  onPageChange,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  tapRef,
  leadingSlot,
  trailingSlot,
  onTrailingReachedChange,
  onLeadingReachedChange,
}: PdfContinuousScrollReaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Measured rendered height (px) of each page once it has rendered. Used as
  // the placeholder height when a page is virtualised out of the render
  // window, so collapsing a rendered page back to a placeholder does not shift
  // content above the viewport and snap the scroll position when scrolling
  // settles. Without it, off-screen pages revert to a fixed 800px guess that
  // rarely matches their real (zoom-dependent) height.
  const pageHeightsRef = useRef<Map<number, number>>(new Map());
  // Intrinsic page size (PDF points) per page, learned from react-pdf's
  // onLoadSuccess. Lets us reserve each page's exact rendered height before the
  // canvas paints, so a page entering the render window — or finishing its draw
  // — never resizes its box and shifts the scroll position.
  const pageOrigRef = useRef<Map<number, PdfPageOrig>>(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasScrolledToInitialRef = useRef(false);
  const lastReportedPageRef = useRef<number>(0);
  // Edge-tracking for the transition panels so reached-change callbacks only
  // fire on transitions, plus a stable view of the config for the listener.
  const trailingReachedRef = useRef(false);
  const leadingReachedRef = useRef(false);
  const reachConfigRef = useRef({
    hasLeading: leadingSlot != null,
    hasTrailing: trailingSlot != null,
    totalPages,
    onLeadingReachedChange,
    onTrailingReachedChange,
    onPageChange,
  });
  reachConfigRef.current = {
    hasLeading: leadingSlot != null,
    hasTrailing: trailingSlot != null,
    totalPages,
    onLeadingReachedChange,
    onTrailingReachedChange,
    onPageChange,
  };

  // State
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set());
  const [currentVisiblePage, setCurrentVisiblePage] = useState(initialPage);
  const [containerWidth, setContainerWidth] = useState(0);
  const [pdfLoaded, setPdfLoaded] = useState(false);
  // A representative intrinsic page size, set from the first page that loads.
  // PDF pages are usually uniform, so this lets us reserve a good height for
  // pages that haven't entered the render window yet (instead of a flat guess).
  // Stored as state (not a ref) so learning it triggers a re-render.
  const [representativeOrig, setRepresentativeOrig] =
    useState<PdfPageOrig | null>(null);

  // Reader store actions
  const goToPage = useReaderStore((state) => state.goToPage);

  // PDF file URL
  const pdfUrl = useMemo(() => `/api/v1/books/${bookId}/file`, [bookId]);

  // Calculate page dimensions based on zoom level
  const getPageDimensions = useCallback(() => {
    if (!containerWidth) {
      return { width: undefined, height: undefined };
    }

    const padding = 40;
    const availableWidth = containerWidth - padding;

    switch (zoomLevel) {
      case "fit-page":
      case "fit-width":
        return { width: availableWidth, height: undefined };
      case "50%":
        return { scale: 0.5 };
      case "75%":
        return { scale: 0.75 };
      case "100%":
        return { scale: 1.0 };
      case "125%":
        return { scale: 1.25 };
      case "150%":
        return { scale: 1.5 };
      case "200%":
        return { scale: 2.0 };
      default:
        return { width: availableWidth, height: undefined };
    }
  }, [containerWidth, zoomLevel]);

  const pageDimensions = useMemo(
    () => getPageDimensions(),
    [getPageDimensions],
  );

  // Determine which pages should be rendered (visible + buffer)
  const pagesToRender = useMemo(() => {
    if (visiblePages.size === 0) {
      // If no pages visible yet, render around initial page
      const start = Math.max(1, initialPage - preloadBuffer);
      const end = Math.min(totalPages, initialPage + preloadBuffer);
      return new Set(
        Array.from({ length: end - start + 1 }, (_, i) => start + i),
      );
    }

    const minVisible = Math.min(...visiblePages);
    const maxVisible = Math.max(...visiblePages);

    // Render visible pages plus buffer
    const start = Math.max(1, minVisible - preloadBuffer);
    const end = Math.min(totalPages, maxVisible + preloadBuffer);
    return new Set(
      Array.from({ length: end - start + 1 }, (_, i) => start + i),
    );
  }, [visiblePages, initialPage, totalPages, preloadBuffer]);

  // Track ResizeObserver instance for cleanup
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Ref callback to set up ResizeObserver when container is attached
  // This solves the timing issue where the effect runs before ref is attached
  const setContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      // Clean up previous observer
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }

      // Update the ref
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current =
        element;

      // Wire the tap ref to the element that actually scrolls so tap-to-toggle
      // navigation works without breaking native scrolling.
      tapRef?.(element);

      // Set up new observer if element exists
      if (element) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            setContainerWidth(entry.contentRect.width);
          }
        });
        resizeObserver.observe(element);
        resizeObserverRef.current = resizeObserver;
      }
    },
    [tapRef],
  );

  // Cleanup ResizeObserver on unmount
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Set up intersection observer
  useEffect(() => {
    const options: IntersectionObserverInit = {
      root: containerRef.current,
      rootMargin: "100px 0px",
      threshold: [0, 0.1, 0.5, 0.9, 1],
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const newVisiblePages = new Set(visiblePages);
      let topMostPage = currentVisiblePage;
      let topMostRatio = 0;

      for (const entry of entries) {
        const pageNum = Number(entry.target.getAttribute("data-page"));
        if (Number.isNaN(pageNum)) continue;

        if (entry.isIntersecting) {
          newVisiblePages.add(pageNum);
          const rect = entry.boundingClientRect;
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const visibleTop = Math.max(rect.top, containerRect.top);
            const visibleBottom = Math.min(rect.bottom, containerRect.bottom);
            const visibleHeight = visibleBottom - visibleTop;
            const ratio = visibleHeight / rect.height;

            if (rect.top <= containerRect.top + 100 && ratio > topMostRatio) {
              topMostPage = pageNum;
              topMostRatio = ratio;
            }
          }
        } else {
          newVisiblePages.delete(pageNum);
        }
      }

      setVisiblePages(newVisiblePages);
      if (topMostPage !== currentVisiblePage) {
        setCurrentVisiblePage(topMostPage);
      }
    }, options);

    const currentObserver = observerRef.current;
    for (const [, element] of pageRefs.current) {
      currentObserver.observe(element);
    }

    return () => {
      currentObserver.disconnect();
    };
  }, [currentVisiblePage, visiblePages]);

  // Report page changes with debouncing
  useEffect(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (currentVisiblePage !== lastReportedPageRef.current) {
        lastReportedPageRef.current = currentVisiblePage;
        goToPage(currentVisiblePage);
        onPageChange?.(currentVisiblePage);
      }
    }, SCROLL_DEBOUNCE_MS);

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [currentVisiblePage, goToPage, onPageChange]);

  // Transition-panel reach detection. When a trailing "Next Chapter" panel is
  // present and the user scrolls to the very bottom, force the reported page to
  // the last page so progress reaches 100% and the book is marked complete (the
  // last real page sits above the 100dvh panel and stops intersecting, so the
  // observer never reports it). Mirrors the comic ContinuousScrollReader.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let timeout: NodeJS.Timeout | null = null;
    const evaluate = () => {
      const reach = reachConfigRef.current;
      if (!reach.hasTrailing && !reach.hasLeading) return;
      const atBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - BOUNDARY_TOLERANCE;
      const atTop = container.scrollTop <= BOUNDARY_TOLERANCE;

      if (reach.hasTrailing && atBottom !== trailingReachedRef.current) {
        trailingReachedRef.current = atBottom;
        if (atBottom) {
          const last = reach.totalPages;
          lastReportedPageRef.current = last;
          setCurrentVisiblePage(last);
          goToPage(last);
          reach.onPageChange?.(last);
        }
        reach.onTrailingReachedChange?.(atBottom);
      }
      if (reach.hasLeading && atTop !== leadingReachedRef.current) {
        leadingReachedRef.current = atTop;
        reach.onLeadingReachedChange?.(atTop);
      }
    };
    const onScroll = () => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(evaluate, SCROLL_DEBOUNCE_MS);
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    container.addEventListener("wheel", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      container.removeEventListener("wheel", onScroll);
      if (timeout) clearTimeout(timeout);
    };
    // Bind once; the listener reads fresh config via reachConfigRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goToPage]);

  // Scroll to initial page on mount. A leading panel sits above page 1, so even
  // when starting at page 1 we must scroll down past it to land on the first
  // page rather than opening on the "Previous Chapter" panel.
  const hasLeadingSlot = leadingSlot != null;
  useEffect(() => {
    if (hasScrolledToInitialRef.current) return;
    if (!pdfLoaded) return;
    const targetPage = Math.max(1, initialPage);
    if (targetPage <= 1 && !hasLeadingSlot) {
      hasScrolledToInitialRef.current = true;
      return;
    }

    const targetRef = pageRefs.current.get(targetPage);
    if (targetRef && containerRef.current) {
      hasScrolledToInitialRef.current = true;
      targetRef.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }, [initialPage, pdfLoaded, hasLeadingSlot]);

  // Register page ref with observer
  const registerPageRef = useCallback(
    (pageNumber: number, element: HTMLDivElement | null) => {
      if (element) {
        pageRefs.current.set(pageNumber, element);
        observerRef.current?.observe(element);
      } else {
        const existing = pageRefs.current.get(pageNumber);
        if (existing) {
          observerRef.current?.unobserve(existing);
        }
        pageRefs.current.delete(pageNumber);
      }
    },
    [],
  );

  // Capture a page's intrinsic size as soon as react-pdf loads it (before the
  // canvas paints), so we can reserve its exact height. The first page to load
  // also seeds the representative size used for not-yet-seen pages.
  const handlePageLoadSuccess = useCallback(
    (
      pageNumber: number,
      page: { originalWidth: number; originalHeight: number },
    ) => {
      const orig: PdfPageOrig = {
        width: page.originalWidth,
        height: page.originalHeight,
      };
      pageOrigRef.current.set(pageNumber, orig);
      setRepresentativeOrig((prev) => prev ?? orig);
    },
    [],
  );

  // Remember a page's rendered height once react-pdf finishes drawing it, so
  // virtualising it out later keeps the layout stable (see pageHeightsRef).
  const handlePageRenderSuccess = useCallback((pageNumber: number) => {
    const pageEl = pageRefs.current.get(pageNumber);
    if (pageEl) {
      const height = pageEl.offsetHeight;
      if (height > 0) {
        pageHeightsRef.current.set(pageNumber, height);
      }
    }
  }, []);

  // Handle PDF load success
  const handleDocumentLoadSuccess = useCallback(
    (pdf: { numPages: number }) => {
      setPdfLoaded(true);
      onDocumentLoadSuccess?.(pdf);
    },
    [onDocumentLoadSuccess],
  );

  // Custom text renderer for search highlighting
  const customTextRenderer = useMemo(() => {
    if (!searchText) return undefined;
    return ({ str }: { str: string }) => {
      const regex = new RegExp(
        `(${searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi",
      );
      const parts = str.split(regex);
      return parts
        .map((part) =>
          regex.test(part)
            ? `<mark style="background-color: yellow; padding: 0;">${part}</mark>`
            : part,
        )
        .join("");
    };
  }, [searchText]);

  // Check if fit modes require container width that isn't ready yet
  const isFitMode = zoomLevel === "fit-page" || zoomLevel === "fit-width";
  const containerReady = containerWidth > 0;
  // Width available to a page in fit modes: container minus the inner padding
  // (20px each side). Matches getPageDimensions so reserved heights line up
  // exactly with what react-pdf actually renders.
  const availableWidth = containerWidth - 40;

  if (totalPages === 0) {
    return (
      <Center style={{ width: "100%", height: "100dvh" }}>
        <Text c="dimmed">This PDF has no pages</Text>
      </Center>
    );
  }

  return (
    <Box
      ref={setContainerRef}
      data-testid="pdf-continuous-scroll-container"
      style={{
        width: "100%",
        height: "100%",
        overflow: "auto",
        backgroundColor: BACKGROUND_COLORS[backgroundColor],
        // Let the browser keep the scroll position pinned when content above
        // the viewport changes size. Combined with exact reserved heights, this
        // prevents scroll jumps with no manual scrollTop math. (Unsupported on
        // iOS Safari, which is why the exact reservation below is primary.)
        overflowAnchor: "auto",
      }}
    >
      <Document
        file={pdfUrl}
        onLoadSuccess={handleDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        loading={
          <Center
            style={{
              width: "100%",
              height: "100%",
              minHeight: "calc(100dvh - 128px)",
              backgroundColor: "transparent",
            }}
          >
            <Loader size="lg" color="gray" />
          </Center>
        }
      >
        {/* Wait for container dimensions before rendering pages in fit modes */}
        {isFitMode && !containerReady ? (
          <Center
            style={{
              width: "100%",
              height: "100%",
              minHeight: "calc(100dvh - 128px)",
              backgroundColor: "transparent",
            }}
          >
            <Loader size="lg" color="gray" />
          </Center>
        ) : (
          <Box
            data-testid="pdf-continuous-scroll-inner"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: pageGap,
              padding: "20px",
            }}
          >
            {leadingSlot && (
              <Box
                data-testid="pdf-continuous-leading-slot"
                style={{ width: "100%" }}
              >
                {leadingSlot}
              </Box>
            )}
            {Array.from({ length: totalPages }, (_, i) => {
              const pageNumber = i + 1;
              const shouldRender = pagesToRender.has(pageNumber);
              const hasRendered = pageHeightsRef.current.has(pageNumber);
              // Reserve each page's exact rendered height before its canvas
              // paints, so entering the render window, finishing the draw, and
              // virtualising back out are all layout-neutral and never shift the
              // scroll position. Prefer this page's intrinsic size, then the
              // representative size, then the last measured height, then a guess.
              const orig =
                pageOrigRef.current.get(pageNumber) ?? representativeOrig;
              const exactHeight = orig
                ? pdfPageReservedHeight({ zoomLevel, availableWidth, orig })
                : null;
              const measuredHeight = pageHeightsRef.current.get(pageNumber);
              const reservedHeight =
                exactHeight != null && exactHeight > 0
                  ? `${exactHeight}px`
                  : measuredHeight
                    ? `${measuredHeight}px`
                    : "800px";

              return (
                <Box
                  key={pageNumber}
                  ref={(el) => registerPageRef(pageNumber, el)}
                  data-page={pageNumber}
                  data-testid={`pdf-page-container-${pageNumber}`}
                  style={{
                    width: "100%",
                    // Keep the reserved height until the page has actually
                    // rendered, so the loading→canvas transition doesn't
                    // collapse the box and shift everything below it.
                    minHeight:
                      shouldRender && hasRendered ? undefined : reservedHeight,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {shouldRender ? (
                    <Page
                      pageNumber={pageNumber}
                      width={pageDimensions.width}
                      height={pageDimensions.height}
                      scale={
                        "scale" in pageDimensions
                          ? pageDimensions.scale
                          : undefined
                      }
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      onLoadSuccess={(page) =>
                        handlePageLoadSuccess(pageNumber, page)
                      }
                      onRenderSuccess={() =>
                        handlePageRenderSuccess(pageNumber)
                      }
                      loading={
                        <Center
                          style={{
                            width: "100%",
                            height: reservedHeight,
                            backgroundColor: "transparent",
                          }}
                        >
                          <Loader size="md" color="gray" />
                        </Center>
                      }
                      customTextRenderer={customTextRenderer}
                    />
                  ) : (
                    <Box
                      data-testid={`pdf-page-placeholder-${pageNumber}`}
                      style={{
                        width: "100%",
                        height: reservedHeight,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Text c="dimmed" size="sm">
                        Page {pageNumber}
                      </Text>
                    </Box>
                  )}
                </Box>
              );
            })}
            {trailingSlot && (
              <Box
                data-testid="pdf-continuous-trailing-slot"
                style={{ width: "100%" }}
              >
                {trailingSlot}
              </Box>
            )}
          </Box>
        )}
      </Document>
    </Box>
  );
}
